// src/pages/OnboardingWizard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  IconButton,
  Divider,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  School as SchoolIcon,
  Celebration as CelebrationIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Sms as SmsIcon,
  Email as EmailIcon,
  Phone as VoiceIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../services/api';

// Phone number formatting
const formatPhoneInput = (value) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length === 0) return '';
  if (numbers.length <= 3) return `(${numbers}`;
  if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
};

const OnboardingWizard = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form data
  const [clientData, setClientData] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [addedClient, setAddedClient] = useState(null);

  // Check if already completed onboarding
  useEffect(() => {
    if (user?.onboarding_status?.completed) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const steps = [
    {
      label: 'Welcome',
      icon: <CelebrationIcon />,
      description: 'Learn about Second Clear'
    },
    {
      label: 'Add a Loved One',
      icon: <PersonIcon />,
      description: 'Add someone to protect'
    },
    {
      label: 'Verify Connection',
      icon: <CheckCircleIcon />,
      description: 'Confirm their phone number'
    },
    {
      label: 'How It Works',
      icon: <SchoolIcon />,
      description: 'Quick tutorial'
    }
  ];

  // Handle adding a person
  const handleAddClient = async () => {
    setLoading(true);
    setError('');

    try {
      // Format phone to E.164
      const phoneDigits = clientData.phone.replace(/\D/g, '');
      const formattedPhone = phoneDigits.length === 10 ? `+1${phoneDigits}` : `+${phoneDigits}`;

      const response = await userAPI.addClient({
        name: clientData.name.trim() || undefined,
        phone: formattedPhone,
        email: clientData.email || undefined
      });

      setAddedClient(response.data.client);
      setSuccess('Added successfully!');

      // If verification is needed, stay here; otherwise advance
      if (response.data.verificationRequired) {
        setVerificationSent(true);
      }

      setActiveStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add');
    } finally {
      setLoading(false);
    }
  };

  // Handle verification
  const handleVerify = async () => {
    setLoading(true);
    setError('');
    
    try {
      await userAPI.verifyClient(addedClient.id, verificationCode);
      setSuccess('Verification successful!');
      setActiveStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  // Skip verification (for beta testing)
  const handleSkipVerification = () => {
    setActiveStep(3);
  };

  // Complete onboarding
  const handleComplete = async () => {
    setLoading(true);
    try {
      await userAPI.completeOnboarding();
      await refreshUser();
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to complete onboarding:', err);
      navigate('/dashboard');
    }
  };

  // Render step content
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Welcome to Second Clear! 👋
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We're here to help you protect your loved ones from fraud and scams.
            </Typography>
            
            <Grid container spacing={3} sx={{ my: 3 }}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                  <SmsIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>SMS Analysis</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Forward suspicious texts and get instant analysis
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                  <VoiceIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>Voice Calls</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Call our hotline to get guidance on suspicious calls
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                  <EmailIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>Email Checks</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Forward suspicious emails to check@secondclear.com
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>How it works:</strong> Your loved one forwards suspicious messages to us. 
                We analyze them using pattern detection and send back a clear, calm explanation 
                of any warning signs—without ever declaring something "fraud" or "safe."
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => setActiveStep(1)}
              >
                Let's Get Started
              </Button>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Add a Loved One to Protect
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Enter the details of the person you want to help protect from scams.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Box component="form" sx={{ maxWidth: 400 }}>
              <TextField
                fullWidth
                label="Their Name (optional)"
                value={clientData.name}
                onChange={(e) => setClientData({ ...clientData, name: e.target.value })}
                margin="normal"
                placeholder="e.g., Mom, Dad, Grandma"
              />
              <TextField
                fullWidth
                label="Their Phone Number"
                value={clientData.phone}
                onChange={(e) => setClientData({ ...clientData, phone: formatPhoneInput(e.target.value) })}
                margin="normal"
                required
                placeholder="(555) 123-4567"
                helperText="They'll receive a verification code"
              />
              <TextField
                fullWidth
                label="Their Email (Optional)"
                type="email"
                value={clientData.email}
                onChange={(e) => setClientData({ ...clientData, email: e.target.value })}
                margin="normal"
                placeholder="optional@example.com"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => setActiveStep(0)}
              >
                Back
              </Button>
              <Button
                variant="contained"
                endIcon={loading ? <CircularProgress size={20} /> : <ArrowForwardIcon />}
                onClick={handleAddClient}
                disabled={loading || !clientData.phone}
              >
                {loading ? 'Adding...' : 'Add & Continue'}
              </Button>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Verify Connection
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We sent a verification code to {addedClient?.name}'s phone. 
              Ask them for the code to confirm the connection.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Box sx={{ maxWidth: 400 }}>
              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                margin="normal"
                placeholder="123456"
                inputProps={{ 
                  style: { fontSize: '1.5rem', letterSpacing: '0.5rem', textAlign: 'center' },
                  maxLength: 6
                }}
              />
            </Box>

            <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
              <Typography variant="body2">
                <strong>Tip:</strong> If they didn't receive the code, you can resend it or 
                skip verification for now. You can verify later in Settings.
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => setActiveStep(1)}
              >
                Back
              </Button>
              <Button
                variant="contained"
                endIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                onClick={handleVerify}
                disabled={loading || verificationCode.length !== 6}
              >
                {loading ? 'Verifying...' : 'Verify'}
              </Button>
              <Button
                variant="text"
                onClick={handleSkipVerification}
                sx={{ ml: 'auto' }}
              >
                Skip for Now
              </Button>
            </Box>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Quick Tutorial 📚
            </Typography>
            
            <Box sx={{ my: 3 }}>
              <Typography variant="h6" gutterBottom>
                How {addedClient?.name || 'Your Loved One'} Can Use Second Clear:
              </Typography>
              
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Chip label="1" color="primary" size="small" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Suspicious Text Message?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Forward it to our number: <strong>{process.env.REACT_APP_SERVICE_PHONE || '(XXX) XXX-XXXX'}</strong>
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Chip label="2" color="primary" size="small" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Suspicious Phone Call?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Call our hotline and describe what they said. We'll help analyze it.
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Chip label="3" color="primary" size="small" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Suspicious Email?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Forward it to: <strong>check@secondclear.com</strong>
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              What You'll See as a Caregiver:
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <SecurityIcon color="primary" sx={{ mb: 1 }} />
                    <Typography variant="subtitle2" fontWeight={600}>Messages Dashboard</Typography>
                    <Typography variant="body2" color="text.secondary">
                      See all messages your loved ones have sent for analysis, 
                      including risk assessments.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <EmailIcon color="primary" sx={{ mb: 1 }} />
                    <Typography variant="subtitle2" fontWeight={600}>Alert Notifications</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Get notified immediately when a high-risk message is detected.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>You're all set!</strong> Your dashboard will show all activity 
                from {addedClient?.name || 'your loved ones'}. You can always add more people 
                from the People page.
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => setActiveStep(2)}
              >
                Back
              </Button>
              <Button
                variant="contained"
                size="large"
                endIcon={loading ? <CircularProgress size={20} /> : <CelebrationIcon />}
                onClick={handleComplete}
                disabled={loading}
              >
                {loading ? 'Finishing...' : 'Go to Dashboard'}
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        py: 4
      }}
    >
      <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
        {/* Progress */}
        <Box sx={{ mb: 4 }}>
          <LinearProgress 
            variant="determinate" 
            value={((activeStep + 1) / steps.length) * 100} 
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            Step {activeStep + 1} of {steps.length}
          </Typography>
        </Box>

        {/* Stepper (for larger screens) */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  icon={
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: index <= activeStep ? 'primary.main' : 'grey.300',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {step.icon}
                    </Box>
                  }
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Main Content */}
        <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
          {renderStepContent(activeStep)}
        </Paper>

        {/* Skip link */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button
            variant="text"
            size="small"
            onClick={handleComplete}
            sx={{ color: 'text.secondary' }}
          >
            Skip Setup & Go to Dashboard
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingWizard;
